import { useEffect, useState } from 'react';

import type { Quake } from '../api/types';

export interface PriceSample {
  at: number;
  price: number;
}

export type PriceSamplesByAsset = Record<string, PriceSample[]>;

const MAX_SAMPLE_AGE_MS = 60 * 60 * 1_000;

export function useLivePriceSamples(movers: readonly Quake[]) {
  const [samples, setSamples] = useState<PriceSamplesByAsset>({});

  useEffect(() => {
    if (movers.length === 0) return;

    const now = Date.now();
    setSamples((previous) => {
      let changed = false;
      const next: PriceSamplesByAsset = { ...previous };
      const touched = new Set<string>();

      for (const mover of movers) {
        if (mover.asset_id === '' || mover.new_price <= 0 || mover.new_price > 1) continue;
        const at = sampleTime(mover.timestamp, now);
        const existing = next[mover.asset_id] ?? [];
        const last = existing.at(-1);
        if (last && Math.abs(last.at - at) < 500 && last.price === mover.new_price) {
          touched.add(mover.asset_id);
          continue;
        }

        next[mover.asset_id] = [...existing, { at, price: mover.new_price }].filter(
          (sample) => now - sample.at <= MAX_SAMPLE_AGE_MS,
        );
        touched.add(mover.asset_id);
        changed = true;
      }

      for (const [assetID, assetSamples] of Object.entries(previous)) {
        if (touched.has(assetID)) continue;
        const pruned = assetSamples.filter((sample) => now - sample.at <= MAX_SAMPLE_AGE_MS);
        if (pruned.length !== assetSamples.length) {
          next[assetID] = pruned;
          changed = true;
        }
      }

      return changed ? next : previous;
    });
  }, [movers]);

  return samples;
}

function sampleTime(value: string, fallback: number) {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(parsed, fallback);
}
